// Country dialing codes for the phone-number inputs. Ethiopia first, then the
// most common neighbours and international codes.
export interface CountryCode {
  iso: string;
  label: string;
  code: string;
}

export const COUNTRY_CODES: CountryCode[] = [
  { iso: "ET", label: "ET +251", code: "+251" },
  { iso: "US", label: "US +1", code: "+1" },
  { iso: "GB", label: "GB +44", code: "+44" },
  { iso: "KE", label: "KE +254", code: "+254" },
  { iso: "SD", label: "SD +249", code: "+249" },
  { iso: "SS", label: "SS +211", code: "+211" },
  { iso: "ER", label: "ER +291", code: "+291" },
  { iso: "DJ", label: "DJ +253", code: "+253" },
  { iso: "SO", label: "SO +252", code: "+252" },
  { iso: "AE", label: "AE +971", code: "+971" },
  { iso: "SA", label: "SA +966", code: "+966" },
  { iso: "IN", label: "IN +91", code: "+91" },
  { iso: "DE", label: "DE +49", code: "+49" },
  { iso: "FR", label: "FR +33", code: "+33" },
  { iso: "IT", label: "IT +39", code: "+39" },
  { iso: "CN", label: "CN +86", code: "+86" },
  { iso: "CA", label: "CA +1", code: "+1" },
  { iso: "AU", label: "AU +61", code: "+61" },
];

// Combines a selected country code with a locally typed number into an E.164
// string. Tolerant of users who type the country code or a leading zero.
export const normalizePhoneNumber = (
  countryCode: string,
  local: string | null | undefined,
): string => {
  const digits = String(local || "").replace(/\D/g, "");
  const countryDigits = countryCode.replace(/\D/g, "");

  if (!digits) return "";

  // User already typed the full international number.
  if (digits.startsWith(countryDigits)) return `+${digits}`;

  // Otherwise drop any national trunk "0" and prefix the country code.
  return `${countryCode}${digits.replace(/^0+/, "")}`;
};

// Splits a stored E.164 number back into a known dialing code + local part, so
// an edit form can prefill the country-code <select> and the number input.
// Matches the longest code first (e.g. +251 before +1) and falls back to
// Ethiopia (+251) with the raw digits when nothing matches.
export const splitPhoneNumber = (
  phoneNumber: string | null | undefined,
): { code: string; local: string } => {
  const raw = String(phoneNumber || "").trim();
  if (!raw) return { code: "+251", local: "" };

  const normalized = raw.startsWith("+") ? raw : `+${raw.replace(/\D/g, "")}`;

  const byLengthDesc = [...COUNTRY_CODES].sort(
    (a, b) => b.code.length - a.code.length,
  );
  const match = byLengthDesc.find((c) => normalized.startsWith(c.code));

  if (match) {
    return { code: match.code, local: normalized.slice(match.code.length) };
  }
  return { code: "+251", local: normalized.replace(/^\+/, "") };
};
